import { assertUnreachable } from '@sgme/fp';
import { publicationApi } from 'apiConnector/discover';
import { DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY } from 'components/discover/shared';
import { useFetchArrayData } from 'hooks/useFetchData';
import { FC, useCallback, useContext } from 'react';

import { ErrorWithRetry } from '../../shared/ErrorWithRetry';
import { RegionContext } from '../contexts/RegionContext';
import { MainTopTrades } from './MainTopTrades';
import { MainTopTradesItemSkeleton } from './MainTopTradesItem';
import type { TopTradesContainerProps } from './TopTradesContainer';
import { TopTradesItem } from './TopTradesItem';

export const MainTopTradesContainer: FC<TopTradesContainerProps> = ({ fetch }) => {
  const { region } = useContext(RegionContext);
  const fetchPublications = useCallback(() => fetch(region), [fetch, region]);

  const [status, items, retry] = useFetchArrayData(fetchPublications);
  switch (status) {
    case 'success':
      return (
        <MainTopTrades>
          {(items ?? []).map(({ id, title, content, documentLink, pictureUrl, pictureLink }) => (
            <TopTradesItem
              key={id ?? title}
              title={title}
              dimension="lg"
              content={content}
              documentLink={documentLink}
              pictureUrl={pictureUrl}
              pictureLink={pictureLink}
            ></TopTradesItem>
          ))}
        </MainTopTrades>
      );
    case 'loading':
      return (
        <MainTopTrades>
          <MainTopTradesItemSkeleton lines={5} />
          <MainTopTradesItemSkeleton lines={6} className="mb-0" />
        </MainTopTrades>
      );
    case 'error':
      return (
        <MainTopTrades>
          <ErrorWithRetry onRetry={retry} e2e="top-trades-error" msgKey={DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY} />
        </MainTopTrades>
      );
    case 'unauthorized':
    case 'empty':
      return null;

    default:
      return assertUnreachable(status, 'Unhandled status');
  }
};

export const MainTopTradesConnected: FC = () => <MainTopTradesContainer fetch={publicationApi.fetchTopTrades} />;
